import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Twitter, Youtube, Facebook, Instagram, Save, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SocialSettingsPanel() {
  const [links, setLinks] = useState({ twitter: '', youtube: '', facebook: '', instagram: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/social`);
      setLinks(data);
    } catch (err) {
      console.error('Error fetching social links:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLinks(prev => ({ ...prev, [name]: value }));
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setSaving(true);
    try {
      await axios.put(`${API}/settings/social`, links, { withCredentials: true });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { name: 'twitter', label: 'Twitter / X', Icon: Twitter, placeholder: 'https://twitter.com/your-handle' },
    { name: 'youtube', label: 'YouTube', Icon: Youtube, placeholder: 'https://youtube.com/@your-channel' },
    { name: 'facebook', label: 'Facebook', Icon: Facebook, placeholder: 'https://facebook.com/your-page' },
    { name: 'instagram', label: 'Instagram', Icon: Instagram, placeholder: 'https://instagram.com/your-handle' }
  ];

  if (loading) {
    return (
      <div className="bg-white border border-[#E5E5E5] p-6" data-testid="social-settings-loading">
        <div className="text-[#8A8A8A]" >Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E5E5E5] p-6" data-testid="social-settings-panel">
      <div className="mb-6">
        <h2 className="text-2xl font-bold tracking-tight text-[#0A0A0A]" >
          Social Media Links
        </h2>
        <p className="text-sm text-[#8A8A8A] mt-1" >
          Links shown in the website header. Leave empty to hide an icon.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {fields.map(({ name, label, Icon, placeholder }) => (
          <div key={name}>
            <label className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-2" >
              <Icon size={14} />
              {label}
            </label>
            <input
              type="url"
              name={name}
              value={links[name]}
              onChange={handleChange}
              data-testid={`social-input-${name}`}
              placeholder={placeholder}
              className="w-full px-4 py-3 border border-[#E5E5E5] focus:outline-none focus:border-[#E63946] transition-colors"
              
            />
          </div>
        ))}

        {error && (
          <div className="p-3 bg-[#E63946] bg-opacity-10 border border-[#E63946] text-[#E63946] text-sm" data-testid="social-settings-error" >
            {error}
          </div>
        )}

        {success && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-500 text-green-700 text-sm" data-testid="social-settings-success" >
            <CheckCircle2 size={16} />
            Social media links saved successfully.
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          data-testid="social-settings-save-button"
          className="flex items-center gap-2 px-6 py-3 bg-[#E63946] text-white font-bold text-sm tracking-[0.2em] uppercase hover:bg-[#d32f3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          
        >
          <Save size={16} />
          {saving ? 'SAVING...' : 'SAVE LINKS'}
        </button>
      </form>
    </div>
  );
}

export default SocialSettingsPanel;
